import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useGetStorageAccessFlags } from "../../graphql/__generated__/GetStorageAccessFlags.generated";
import type { IconType } from "react-icons";
import {
  HiUserGroup,
  HiChevronDown,
  HiBuildingStorefront,
  HiBuildingOffice2,
  HiCog6Tooth,
  HiTruck,
  // HiCalendarDays,
  HiEllipsisVertical,
  HiOutlineArrowRightOnRectangle,
  HiBellAlert,
} from "react-icons/hi2";
import { MdSpaceDashboard } from "react-icons/md";
import { LuPanelLeftClose, LuPanelLeftOpen } from "react-icons/lu";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import Logo from "../../assets/logo.svg";

interface NavItem {
  label: string;
  icon: IconType;
  path: string;
  children?: { label: string; path: string }[];
}

interface SidebarProps {
  onActiveChange?: (label: string) => void;
}

export default function Sidebar({ onActiveChange }: SidebarProps) {
  const userSessionData = sessionStorage.getItem("data");
  const userSession = userSessionData ? JSON.parse(userSessionData) : { me: null };
  const isSuperAdmin = userSession?.me?.usertype === "SUPERADMIN";
  const isAdmin = userSession?.me?.usertype === "ADMIN" || isSuperAdmin;

  const { data: storageData } = useGetStorageAccessFlags({
    variables: { belongsTo: userSession?.me?.id },
    skip: isAdmin || isSuperAdmin || !userSession?.me?.id,
    pollInterval: 30000,
  });

  const hasQRAccess = storageData?.getStorages?.some((s) => s?.isQRSystemEnabled) ?? false;

  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [];

    // Dashboard
    if (!isSuperAdmin) {
      if (isAdmin) {
        items.push({ label: "Dashboard", icon: MdSpaceDashboard, path: "/app/0" });
      } else {
        items.push({ label: "Dashboard", icon: MdSpaceDashboard, path: "/app/partner/dashboard" });
      }
    }

    if (isSuperAdmin) {
      items.push({ label: "Manage Admins", icon: HiUserGroup, path: "/app/superadmin/admins" });
    } else if (isAdmin) {
      items.push(
        { label: "User", icon: HiUserGroup, path: "/app/1/users" },
        { label: "Storage Unit", icon: HiBuildingStorefront, path: "/app/2/storageunit" },
        { label: "Alerts", icon: HiBellAlert, path: "/app/0/alerts" },
        { label: "Inventory", icon: HiCog6Tooth, path: "/app/3/inventory" },
        { label: "External User", icon: HiUserGroup, path: "/app/4/external-user" },
        { label: "Partner Management", icon: HiBuildingOffice2, path: "/app/admin/partners" },
        { label: "Vehicles", icon: HiTruck, path: "/app/admin/vehicles" }
      );
    } else {
      items.push(
        { label: "Storage Unit", icon: HiBuildingStorefront, path: "/app/partner/storageunit" },
        { label: "Customers", icon: HiUserGroup, path: "/app/partner/customers" },
        { label: "Vehicles", icon: HiTruck, path: "/app/partner/vehicles" }
        // { label: "Bookings", icon: HiCalendarDays, path: "/app/partner/bookings" }
      );
      items.push({ label: "Inventory", icon: HiCog6Tooth, path: "/app/partner/inventory" });

      items.push({ label: "Alerts", icon: HiBellAlert, path: "/app/partner/alerts" });
    }

    return items;
  }, [isAdmin, isSuperAdmin, hasQRAccess]);

  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const toggleSidebar = () => setSidebarOpen(!isSidebarOpen);

  const isNavItemActive = (itemPath: string) => {
    return location.pathname.startsWith(itemPath);
  };

  const activeNavItem = useMemo(() => {
    return (
      [...navItems]
        .sort((a, b) => b.path.length - a.path.length)
        .find((item) => location.pathname.startsWith(item.path)) || navItems[0]
    );
  }, [location.pathname, navItems]);

  useEffect(() => {
    if (onActiveChange && activeNavItem) {
      onActiveChange(activeNavItem.label);
    }
  }, [activeNavItem, onActiveChange]);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/");
  };

  return (
    <Box
      component="aside"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100vh",
        bgcolor: "rgb(247, 247, 247)",
        borderRight: "1px solid #E5E7EB",
        p: "16px 12px",
        width: isSidebarOpen ? 240 : 70,
        transition: "width 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <Box>
        {/* Logo Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isSidebarOpen ? "row" : "column",
            alignItems: "center",
            justifyContent: isSidebarOpen ? "space-between" : "flex-start",
            gap: isSidebarOpen ? 0 : "20px",
            p: "4px 8px",
            mb: "40px",
          }}
        >
          <Box
            onClick={() =>
              navigate(
                isSuperAdmin
                  ? "/app/superadmin/admins"
                  : isAdmin
                    ? "/app/0"
                    : "/app/partner/dashboard"
              )
            }
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              minWidth: 0,
              flex: 1,
            }}
          >
            <Box component="img" src={Logo} alt="SheetX" sx={{ width: 28, height: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: "#1F2937",
                whiteSpace: "nowrap",
                maxWidth: isSidebarOpen ? 120 : 0,
                opacity: isSidebarOpen ? 1 : 0,
                transform: isSidebarOpen ? "translateX(0)" : "translateX(-6px)",
                overflow: "hidden",
                transition: "max-width 0.24s ease, opacity 0.2s ease, transform 0.24s ease",
              }}
            >
              SheetX
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={toggleSidebar}
            sx={{
              borderRadius: "6px",
              border: "1px solid #E5E7EB",
              color: "#6B7280",
            }}
          >
            {isSidebarOpen ? <LuPanelLeftClose size={16} /> : <LuPanelLeftOpen size={16} />}
          </IconButton>
        </Box>

        {/* Navigation Section */}
        <List sx={{ display: "flex", flexDirection: "column", gap: "6px", p: 0 }}>
          {navItems.map((item) => {
            const active = isNavItemActive(item.path);
            return (
              <Box key={item.label}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: "8px",
                    px: "12px",
                    py: "10px",
                    justifyContent: isSidebarOpen ? "initial" : "center",
                    transition: "all 0.24s ease",
                    bgcolor: active ? "#EFF6FF" : "transparent",
                    color: active ? "#1462C8" : "#4B5563",
                    "&:hover": {
                      bgcolor: active ? "#EFF6FF" : "#F3F4F6",
                      color: active ? "#1462C8" : "#1F2937",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isSidebarOpen ? "12px" : 0,
                      justifyContent: "center",
                      color: "inherit",
                      transition: "margin-right 0.24s ease",
                    }}
                  >
                    <item.icon size={20} />
                  </ListItemIcon>
                  <Box
                    sx={{
                      overflow: "hidden",
                      maxWidth: isSidebarOpen ? 160 : 0,
                      opacity: isSidebarOpen ? 1 : 0,
                      transition: "max-width 0.24s ease, opacity 0.2s ease",
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: active ? 600 : 500,
                        whiteSpace: "nowrap",
                      }}
                    />
                  </Box>
                  {item.children && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: isSidebarOpen ? 16 : 0,
                        opacity: isSidebarOpen ? 1 : 0,
                        overflow: "hidden",
                        transition: "width 0.24s ease, opacity 0.2s ease",
                      }}
                    >
                      <HiChevronDown size={14} />
                    </Box>
                  )}
                </ListItemButton>
              </Box>
            );
          })}
        </List>
      </Box>

      {/* User Section */}
      <Box sx={{ pt: "12px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            p: "10px 12px",
            borderRadius: "10px",
            cursor: "pointer",
            justifyContent: isSidebarOpen ? "initial" : "center",
            bgcolor: "#F9FAFB",
            border: "1px solid #F3F4F6",
            "&:hover": { bgcolor: "#F3F4F6", borderColor: "#E5E7EB" },
            transition: "background 0.15s ease",
          }}
          onClick={() =>
            navigate(
              isSuperAdmin ? `/app/0/profile` : isAdmin ? `/app/0/profile` : `/app/partner/profile`
            )
          }
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              fontSize: "13px",
              fontWeight: 700,
              bgcolor: "#111827",
              color: "#FFFFFF",
              flexShrink: 0,
            }}
          >
            {getInitials(userSession?.me?.name || "User")}
          </Avatar>
          <Box
            sx={{
              flex: isSidebarOpen ? 1 : "0 0 0px",
              minWidth: 0,
              maxWidth: isSidebarOpen ? 140 : 0,
              opacity: isSidebarOpen ? 1 : 0,
              overflow: "hidden",
              transition: "max-width 0.24s ease, opacity 0.2s ease",
            }}
          >
            <Typography
              noWrap
              sx={{ fontSize: "13px", fontWeight: 600, color: "#111827", lineHeight: 1.3 }}
            >
              {userSession?.me?.name || "User"}
            </Typography>
            <Typography noWrap sx={{ fontSize: "11px", color: "#6B7280", mt: "2px" }}>
              {userSession?.me?.email || "—"}
            </Typography>
          </Box>
          <Box
            sx={{
              width: isSidebarOpen ? 28 : 0,
              opacity: isSidebarOpen ? 1 : 0,
              overflow: "hidden",
              transition: "width 0.24s ease, opacity 0.2s ease",
              flexShrink: 0,
            }}
          >
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenUserMenu(event);
              }}
              sx={{ color: "#9CA3AF", "&:hover": { color: "#374151" } }}
            >
              <HiEllipsisVertical size={18} />
            </IconButton>
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseUserMenu}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          PaperProps={{
            sx: {
              mb: "4px",
              width: isSidebarOpen ? "216px" : "auto",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              border: "1px solid #E5E7EB",
            },
          }}
        >
          <MenuItem
            onClick={handleLogout}
            sx={{
              fontSize: "13px",
              color: "#DC2626",
              fontWeight: 500,
              gap: "8px",
              "&:hover": { bgcolor: "#FEF2F2" },
            }}
          >
            <HiOutlineArrowRightOnRectangle size={18} />
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
